import {
  HttpClient,
  HttpErrorResponse,
}                         from '@angular/common/http';
import {
  Inject,
  Injectable,
}                         from '@angular/core';
import { Router }         from '@angular/router';
import { AuthService }    from '@auth0/auth0-angular';
import {
  EventSourceMessage,
  fetchEventSource,
}                         from '@microsoft/fetch-event-source';
import { Store }          from '@ngrx/store';
import { firstValueFrom } from 'rxjs';
import { ENVIRONMENT }    from '~app/app.constants';
import {
  Toast,
  ToastService,
}                         from '~app/core';
import * as AppActions    from '~app/state/app.actions';
import {
  ReportEvent,
  ReportRequestStatus,
}                         from '~domain/types';

@Injectable({ providedIn: 'root' })
export class ReportEventsService {
  private connection?: AbortController;
  private readonly pending = new Map<string, ReportRequestStatus>();

  constructor(
    @Inject(ENVIRONMENT) private readonly environment: Environment,
    private readonly authService: AuthService,
    private readonly http: HttpClient,
    private readonly router: Router,
    private readonly store: Store,
    private readonly toastService: ToastService,
  ) {
  }

  start() {
    if (this.connection) {
      return;
    }
    const connection = new AbortController();
    this.connection = connection;
    this.connect(connection);
  }

  stop() {
    this.connection?.abort();
    this.connection = undefined;
    this.pending.clear();
  }

  track(requests: ReportRequestStatus[]) {
    requests.forEach(request => this.pending.set(request.id, request));
    this.checkPendingReportStatuses();
  }

  private connect(connection: AbortController) {
    let retryDelay = 1000;
    void fetchEventSource(`${this.environment.api}/report-events`, {
      signal: connection.signal,
      openWhenHidden: true,
      fetch: (input, init) => this.fetchWithToken(input, init),
      onopen: async response => {
        if (!response.ok) {
          throw new Error(`Report event stream returned ${response.status}`);
        }
        retryDelay = 1000;
        this.checkPendingReportStatuses();
      },
      onmessage: message => this.handleMessage(message),
      onclose: () => {
        throw new Error('Report event stream closed');
      },
      onerror: () => {
        const delay = retryDelay;
        retryDelay = Math.min(retryDelay * 2, 30000);
        return delay;
      },
    }).finally(() => {
      if (this.connection === connection) {
        this.connection = undefined;
      }
    });
  }

  private async fetchWithToken(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    const token = await firstValueFrom(this.authService.getAccessTokenSilently());
    const headers = new Headers(init?.headers);
    headers.set('Authorization', `Bearer ${token}`);
    return fetch(input, { ...init, headers, cache: 'no-store' });
  }

  private handleMessage(message: EventSourceMessage) {
    if (message.event !== 'report-completed' && message.event !== 'report-failed') {
      return;
    }
    const event = JSON.parse(message.data) as ReportEvent;
    this.finish(event.requestId, message.event === 'report-completed');
  }

  private checkPendingReportStatuses() {
    for (const id of this.pending.keys()) {
      void this.checkReportStatus(id);
    }
  }

  private async checkReportStatus(id: string) {
    try {
      const status = await firstValueFrom(this.http.get<ReportRequestStatus>(
        `${this.environment.api}/report-request-status/${id}`,
      ));
      if (status.status === 'DONE' || status.status === 'FAILED') {
        this.finish(id, status.status === 'DONE');
      }
    } catch (error) {
      if (error instanceof HttpErrorResponse && (error.status === 403 || error.status === 404)) {
        this.pending.delete(id);
      }
    }
  }

  private finish(id: string, succeeded: boolean) {
    const request = this.pending.get(id);
    if (!request) {
      return;
    }
    this.pending.delete(id);
    if (!succeeded) {
      this.toastService.show(Toast.Error, 'CREATE_RB_DOCUMENT');
      return;
    }

    this.store.dispatch(AppActions.fetchDocumentBadgeCount());
    this.showCompletedReport(request);
  }

  private showCompletedReport(request: ReportRequestStatus) {
    const url = this.router.url;
    const destination = this.documentsUrl(request);
    const onDocumentsPage = this.isOnDocumentsPage(request, url);
    this.toastService.show(Toast.Success, 'CREATE_RB_DOCUMENT',
      onDocumentsPage ? {} : {
        persistent: true,
        link: { href: destination, labelKey: 'TOAST.SUCCESS.CREATE_RB_DOCUMENT.LINK' },
      });
    if (onDocumentsPage) {
      void this.router.navigateByUrl(url, { onSameUrlNavigation: 'reload' });
    }
  }

  private documentsUrl(request: ReportRequestStatus): string {
    return request.clientId
      ? `/clients/${encodeURIComponent(request.clientId)}/documents`
      : '/documents';
  }

  private isOnDocumentsPage(request: ReportRequestStatus, url: string): boolean {
    return request.clientId
      ? url.match(/^\/clients\/([^/?]+)\/documents(?:[/?]|$)/)?.[1] === request.clientId
      : /^\/documents(?:[/?]|$)/.test(url);
  }

}
